<template>
    <div class="flex flex-col gap-6 pb-4 pt-4">
        <div class="flex flex-col gap-3 rounded-lgr bg-surface p-3 md:p-5">
            <h4 class="body-small-semibold text-ink-muted">{{ t("teamPage.practice.pattern") }}</h4>
            <MarkdownText :text="guidance.overview" class="body-small max-w-prose text-ink" />
        </div>
        <div class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
                <h4 class="body-small-bold block pt-2 text-ink">{{ t("teamPage.practice.discussions") }}</h4>
                <p class="body-small text-ink-muted">{{ t("teamPage.practice.intro") }}</p>
            </div>
            <template v-for="(section, index) in sections" :key="index">
                <BaseAccordion
                    v-if="section.title"
                    :accordion-ids="['topic']"
                    item-class="border border-card-border rounded-lgr"
                    trigger-class="p-3 md:p-5 text-left text-ink"
                    content-class="px-3 pt-4 pb-4 md:px-5 md:pb-5">
                    <template #trigger-topic>
                        <span class="flex min-w-0 flex-col gap-2">
                            <span v-if="section.question" class="body-small text-ink-muted">{{ section.question }}</span>
                            <MarkdownText :text="section.title" inline class="body-small-semibold text-ink" />
                        </span>
                    </template>
                    <template #content-topic>
                        <MarkdownText :text="section.body" :custom-renderer="guidanceRenderer" class="body-small max-w-prose text-ink" />
                    </template>
                </BaseAccordion>
                <MarkdownText v-else :text="section.body" :custom-renderer="guidanceRenderer" class="body-small max-w-prose text-ink" />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { TeamGuidance } from "~vue/api/generated/team-perspective";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { practiceSections, sentenceCaseHeading } from "~vue/Perspective/teamPractice";
import { Lexer, type RendererExtensionFunction, type Tokens } from "marked";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ guidance: TeamGuidance }>();
const { t } = useI18n();
const sections = computed(() => {
    const topics = practiceSections(props.guidance.discussion);
    const tipsIndex = topics.findIndex((section) => section.title === "Team Insights");
    if (tipsIndex >= 0) {
        topics[tipsIndex].title = t("teamPage.practice.topLevelTips");
        if (tipsIndex > 1) {
            const [tips] = topics.splice(tipsIndex, 1);
            topics.splice(1, 0, tips);
        }
    }
    return topics;
});
const guidanceRenderer: Record<string, RendererExtensionFunction> = {
    heading(token) {
        const heading = token as Tokens.Heading;
        const text = this.parser.parseInline(Lexer.lexInline(sentenceCaseHeading(heading.text)));
        return `<h4><span class="body-small-bold block pt-2 text-ink">${text}</span></h4>`;
    },
};
</script>
